"use client";

import * as React from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import {
  FileText,
  Building2,
  FolderKanban,
  Clock,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { needsApi } from "@/lib/api/needs";
import { providersApi } from "@/lib/api/providers";
import { categoriesApi } from "@/lib/api/categories";
import { useAuthStore } from "@/stores/auth-store";
import { cn } from "@/lib/utils/cn";
import { NEED_STATE_COLORS, type NeedState } from "@/types";

export default function DashboardPage() {
  const { user, accessToken, _hasHydrated } = useAuthStore();
  const isAuthenticated = _hasHydrated && !!accessToken;

  const { data: needsData, isLoading: needsLoading } = useQuery({
    queryKey: ["needs", "dashboard"],
    queryFn: () => needsApi.getAll({ pageSize: 100 }),
    enabled: isAuthenticated,
  });

  const { data: providersData, isLoading: providersLoading } = useQuery({
    queryKey: ["providers", "dashboard"],
    queryFn: () => providersApi.getAll({ pageSize: 1 }),
    enabled: isAuthenticated,
  });

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories", "dashboard"],
    queryFn: () => categoriesApi.getAll(),
    enabled: isAuthenticated,
  });

  const isLoading = needsLoading || providersLoading || categoriesLoading;

  // Calculate stats
  const totalNeeds = needsData?.total || 0;
  const totalProviders = providersData?.total || 0;
  const totalCategories = categoriesData?.total || 0;

  const needsByState = React.useMemo(() => {
    if (!needsData?.items) return {};
    return needsData.items.reduce((acc, need) => {
      acc[need.state] = (acc[need.state] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [needsData]);

  const recentNeeds = needsData?.items?.slice(0, 5) || [];

  const StatCard = ({
    title,
    value,
    icon: Icon,
    color = "blue",
    index = 0,
  }: {
    title: string;
    value: number;
    icon: React.ComponentType<{ className?: string }>;
    color?: "blue" | "green" | "purple" | "orange";
    index?: number;
  }) => {
    const colorClasses = {
      blue: { bg: "rgba(30, 183, 255, 0.1)", text: "var(--blue)" },
      green: { bg: "rgba(27, 185, 52, 0.1)", text: "var(--green)" },
      purple: { bg: "rgba(202, 142, 255, 0.1)", text: "var(--purple)" },
      orange: { bg: "rgba(242, 114, 18, 0.1)", text: "var(--orange)" },
    };

    return (
      <Card
        className="card-airframe card-airframe--side-border animate-fade-in-up"
        style={{
          borderLeftColor: colorClasses[color].text,
          animationDelay: `${index * 50}ms`,
        }}
      >
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-lg"
              style={{ backgroundColor: colorClasses[color].bg }}
            >
              <div style={{ color: colorClasses[color].text }}>
                <Icon className="h-6 w-6" />
              </div>
            </div>
            <div className="flex-1">
              {isLoading ? (
                <>
                  <Skeleton className="h-8 w-16 mb-1" />
                  <Skeleton className="h-4 w-24" />
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold" style={{ color: "var(--gray-900)" }}>
                    {value}
                  </div>
                  <p className="text-sm" style={{ color: "var(--gray-500)" }}>
                    {title}
                  </p>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="animate-fade-in-up">
        <h1 className="text-3xl font-bold tracking-tight" style={{ color: "var(--gray-900)" }}>
          Dashboard
        </h1>
        <p style={{ color: "var(--gray-500)" }}>
          Bienvenido, {user?.firstname || user?.email}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Espacios"
          value={totalNeeds}
          icon={FileText}
          color="blue"
          index={0}
        />
        <StatCard
          title="Destinos"
          value={totalProviders}
          icon={Building2}
          color="purple"
          index={1}
        />
        <StatCard
          title="Categorias"
          value={totalCategories}
          icon={FolderKanban}
          color="orange"
          index={2}
        />
        <StatCard
          title="Completadas"
          value={needsByState["Completado"] || 0}
          icon={CheckCircle2}
          color="green"
          index={3}
        />
      </div>

      {/* Status Overview */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="card-airframe animate-fade-in-up" style={{ animationDelay: "200ms" }}>
          <CardHeader className="card-header-airframe">
            <CardTitle className="text-base font-semibold" style={{ color: "var(--gray-800)" }}>
              Estado de Espacios
            </CardTitle>
            <CardDescription style={{ color: "var(--gray-500)" }}>
              Distribucion por estado actual
            </CardDescription>
          </CardHeader>
          <CardContent className="card-body-airframe">
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(needsByState).map(([state, count], index) => (
                  <div
                    key={state}
                    className="flex items-center justify-between p-3 rounded-lg transition-colors hover:bg-gray-50 animate-fade-in-up"
                    style={{ animationDelay: `${250 + index * 50}ms` }}
                  >
                    <Badge className={cn("badge-airframe", NEED_STATE_COLORS[state as NeedState] || "")}>
                      {state}
                    </Badge>
                    <span className="text-lg font-semibold" style={{ color: "var(--gray-900)" }}>
                      {count}
                    </span>
                  </div>
                ))}
                {Object.keys(needsByState).length === 0 && (
                  <div className="flex flex-col items-center py-8">
                    <FileText className="h-12 w-12 mb-3" style={{ color: "var(--gray-300)" }} />
                    <p className="text-sm" style={{ color: "var(--gray-500)" }}>
                      No hay espacios registrados
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="card-airframe animate-fade-in-up" style={{ animationDelay: "250ms" }}>
          <CardHeader className="card-header-airframe flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold" style={{ color: "var(--gray-800)" }}>
                Actividad Reciente
              </CardTitle>
              <CardDescription style={{ color: "var(--gray-500)" }}>
                Ultimos espacios creados
              </CardDescription>
            </div>
            <Link
              href="/needs"
              className="text-sm flex items-center gap-1 hover:underline"
              style={{ color: "var(--blue)" }}
            >
              Ver todas
              <ArrowRight className="h-4 w-4" />
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-5 space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-1">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                {recentNeeds.map((need, index) => (
                  <Link
                    key={need.id}
                    href={`/needs/${need.id}`}
                    className="media px-5 py-3 border-b last:border-b-0 hover:bg-gray-50 transition-colors animate-fade-in-up"
                    style={{
                      borderColor: "var(--gray-200)",
                      animationDelay: `${300 + index * 50}ms`,
                    }}
                  >
                    <div className="media-left">
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-full"
                        style={{ backgroundColor: "rgba(30, 183, 255, 0.1)" }}
                      >
                        <FileText className="h-5 w-5" style={{ color: "var(--blue)" }} />
                      </div>
                    </div>
                    <div className="media-body">
                      <p className="text-sm font-medium truncate" style={{ color: "var(--gray-900)" }}>
                        {need.name}
                      </p>
                      <p className="text-xs" style={{ color: "var(--gray-500)" }}>
                        {new Date(need.createdAt).toLocaleDateString("es-ES")}
                      </p>
                    </div>
                    <div className="media-right">
                      <Badge className={cn("badge-airframe", NEED_STATE_COLORS[need.state] || "")}>
                        {need.state}
                      </Badge>
                    </div>
                  </Link>
                ))}
                {recentNeeds.length === 0 && (
                  <div className="flex flex-col items-center py-8">
                    <Clock className="h-12 w-12 mb-3" style={{ color: "var(--gray-300)" }} />
                    <p className="text-sm" style={{ color: "var(--gray-500)" }}>
                      No hay actividad reciente
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
